import PropTypes from "prop-types";

const objectTypes = [
  "Annotation",
  "ArticleWord",
  "Assignment",
  "BrowseHeader",
  "Classroom",
  "Content", // Represents: ExternalEmbed, Article, Poll, Video, AudioTrack, TextSet
  "ContentBookmark",
  "FullPage", // The entire document provided for a given URL, not just the main contents
  "GeneratedTextAsset",
  "Integration",
  "Poll",
  "PollSubmission",
  "Quiz",
  "QuizQuestion",
  "QuizAnswer",
  "Report",
  "WritePrompt",
  "WritePromptResponse",
  "User",
  "School",
  "Transcript",
];
const actionPrefixes = ["Initiate", "Cancel", "Undo"];
const actionNames = [
  "Add",
  "Archive",
  "Create",
  "Delete",
  "Download",
  "Error",
  "Filter",
  "Finish",
  "Flag",
  "Hide",
  "Interact",
  "Native Print", // Print triggered via an environmentally native interaction
  "Navigate",
  "Page View",
  "Print",
  "Read",
  "Reject",
  "Search",
  "Share",
  "Sign In",
  "Sign Out",
  "Start",
  "Success",
  "Update",
];
const propertyNames = [
  "date_created",
  "dimension_properties",
  "event_name",
  "event_type",
  "id",
  "platform",
  "platform_version",
  "screen_size",
  "user_id",
  "user_unique_id",
];
// Event type should represent what triggered the event.
const eventTypes = [
  "click",
  "hover",
  "impression",
  // Use `native-interaction` if the form of user interaction (e.g. click
  // versus keypress) is not knowable. For example, document printing can be
  // triggered through a keypress or through native file menu selections.
  "native-interaction",
  "page-view",
];
const dimensionPropertyNames = [
  "action_name",
  "action_prefix",
  "application",
  "article_header_content_id",
  "article_story_content_id",
  "captured_url",
  "component_type",
  "content_id",
  "content_ids", // This is an array of the ids of the assigned content (articles, videos etc.)
  "content_properties",
  "content_type",
  "context",
  "current_url",
  "count_results_retrieved",
  "count_results_total",
  "description",
  "error_code",
  "file_url", // Used for image, video, & audio file urls
  "hash",
  "homepage_id",
  "layout_index",
  "legacy_event_name",
  "object_id",
  "object_type",
  "parent_row",
  "previous_url",
  "raw_query_string",
  "recommendation_set_ids",
  "referrer",
  "reading_percentage",
  "report_filters",
  "report_type",
  "result_identifiers",
  "request_type",
  "row_slug",
  "screen_size",
  "scrollX",
  "scrollY",
  "search_id",
  "search_term",
  "search",
  "selected_option",
  "oulipo_content_id",
  "oulipo_block_id",
  "oulipo_block_sibling_index",
  "oulipo_page_id",
  "oulipo_page_type",
  "oulipo_parent_block_id",
  "oulipo_response_id",
  "oulipo_tracking_id",
  "selected_word_count",
  "previous_content_id",
  "external_referrer",
  "internal_referrer",
  "url_query_parameters",
  "user_roles",
];

const propTypes = {
  /**
   * These three props for the event name.
   * EventName = ObjectName + ActionPrefix + ActionName
   *
   * Ex. 'Content Initiate Assign', 'Assignment Cancel Create', 'WritePrompt Create'
   */
  objectType: PropTypes.oneOf(objectTypes),
  actionPrefix: PropTypes.oneOf(actionPrefixes),
  actionName: PropTypes.oneOf(actionNames).isRequired,

  // Define children wrapper html element, default is div.
  childrenWrapperElement: PropTypes.string,

  // Properties according to new eventStream SLA.
  streamProperties: PropTypes.object,

  userId: PropTypes.string,

  /**
   * Control which event types to track.
   */
  trackView: PropTypes.bool, // Rendered on page.
  trackHover: PropTypes.bool,
  trackClick: PropTypes.bool,
  trackImpression: PropTypes.bool, // Visible in view port.

  eventSource: PropTypes.string, // Impression source.

  /**
   * Component interaction functions to pass through to wrapped component.
   */
  onClick: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,

  // This is passed in automatically when you use ref
  forwardedRef: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
};

export default propTypes;
export {
  objectTypes,
  actionPrefixes,
  actionNames,
  propertyNames,
  eventTypes,
  dimensionPropertyNames,
};
